import {
  getDatabaseCodeSafely,
  getObjectId,
  isBefore,
  isDatabaseCode,
  SiteEntity,
  TimeGroup,
  toTimeGroup,
  TransportCategory
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Group, Modal, ModalProps} from "@mantine/core";
import {useCallback} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {TimeGroupSelect} from "../../select/entity/TimeGroupSelect/TimeGroupSelect";
import {DefaultTFuncReturn} from "i18next";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";

interface MeterFlowVolumeReviewSearchFormProps {
  options?: MeterFlowVolumeReviewSearchFormOptions;
  defaultValues?: Partial<MeterFlowVolumeReviewSearchFormValues>;
  onSubmit?: (values: MeterFlowVolumeReviewSearchFormValues) => void;
  submitting?: boolean;
}

export interface MeterFlowVolumeReviewSearchFormOptions {
  regionId?: string
  category?: TransportCategory
}

export interface MeterFlowVolumeReviewSearchFormValues {
  fromDate: Date
  toDate?: Date | undefined
  site?: SiteEntity | string;
  timeGroup?: TimeGroup | string
}

const getDefaultValues = (defaultValues: Partial<MeterFlowVolumeReviewSearchFormValues> | undefined) => {
  return {
    fromDate: defaultValues?.fromDate,
    toDate: defaultValues?.toDate,
    site: getObjectId(defaultValues?.site),
    timeGroup: getDatabaseCodeSafely(defaultValues?.timeGroup),
  }
}

export const MeterFlowVolumeReviewSearchForm = (props: MeterFlowVolumeReviewSearchFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {
      toDate: (value, values) => {
        let error: string | undefined | DefaultTFuncReturn = undefined
        if (value && values.fromDate && isBefore(value, values.fromDate)) {
          error = t<string>("common.form.validation.todatebeforefrom", "The 'to' date can't be before the 'from' date")
        }
        return error
      }
    }
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        fromDate: values.fromDate!,
        toDate: values.toDate,
        site: values.site,
        timeGroup: values.timeGroup ? toTimeGroup(values.timeGroup) : undefined,
      })
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Group align="flex-end">
        <Grid>
          <Grid.Col span={6}>
            <DateInput
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.ui.meterflowvolumereview.search.form.field.fromdate", "From Date")}
              placeholder={t<string>("common.ui.meterflowvolumereview.search.form.field.fromdate", "From Date")}
              clearable
              withAsterisk
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              required
              {...form.getInputProps("fromDate")} />
          </Grid.Col>
          <Grid.Col span={6}>
            <DateInput
              miw={100}
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.ui.meterflowvolumereview.search.form.field.todate", "To Date")}
              placeholder={t<string>("common.ui.meterflowvolumereview.search.form.field.todate", "To Date")}
              clearable
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              {...form.getInputProps("toDate")} />
          </Grid.Col>
          <Grid.Col span={12}>
            <TimeGroupSelect
              label={t("common.ui.meterflowvolumereview.search.form.field.timegroup", "Time Group")}
              placeholder={t<string>("common.ui.meterflowvolumereview.search.form.field.timegroup", "Time Group")}
              clearable
              withinPortal={true}
              {...form.getInputProps("timeGroup")} />
          </Grid.Col>
        </Grid>
      </Group>
      <Divider my="sm" variant="dotted"/>
      <SiteSelect
        {...form.getInputProps("site")}
        label={t("common.ui.meterflowvolumereview.search.form.field.site", "Site")}
        placeholder={t<string>("common.ui.meterflowvolumereview.search.form.field.object.site", "Site")}
        clearable={true}
        withinPortal={true}
        selector={{
          regionId: options?.regionId
        }}
      />
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface MeterFlowVolumeReviewSearchFormModalProps extends MeterFlowVolumeReviewSearchFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const MeterFlowVolumeReviewSearchFormModal = (props: MeterFlowVolumeReviewSearchFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <MeterFlowVolumeReviewSearchForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        submitting={submitting}/>
    </Modal>
  );
};
